import { useMemo, useRef, useState } from 'react'
import { useModalShell } from './useModalShell'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Search, Upload, X } from 'lucide-react'
import { describeSaving, resizeForUpload } from '../api/imageResize'
import { displayName, fetchMedia, fileSize, uploadMedia, type MediaAsset } from '../api/media'
import StudioImage from './StudioImage'

/**
 * Tonya's photo library, as a chooser (§4.7, D-57).
 *
 * **Choosing a picture is picking one that already exists, far more often than
 * uploading a new one.** Twenty years of studio photography came across from
 * Wix, so the library opens on what she has and keeps upload as a second act —
 * the reverse would make her hunt her own files on a laptop every time.
 *
 * Deliberately not a page of its own. The question "which photo?" is only ever
 * asked while editing something, and a separate screen would mean leaving the
 * thing being edited to answer it.
 */
export default function MediaPicker({
  current,
  onChoose,
  onClose,
}: {
  /** The URL currently in the field, so the one in use reads as chosen. */
  current: string
  onChoose: (url: string) => void
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const fileInput = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [error, setError] = useState<string | null>(null)

  const { data, isPending, isError } = useQuery({ queryKey: ['media'], queryFn: fetchMedia })

  const [shrinking, setShrinking] = useState<string | null>(null)

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      /*
       * Shrunk here, in the browser, before anything is sent (D-126). A camera
       * original is 12 MB and a hero image needs 300 KB — so this is both the
       * upload she waits on and the bytes every visitor downloads afterwards.
       */
      setShrinking('Preparing...')

      const prepared = await Promise.all(files.map(resizeForUpload))
      const saved = prepared.filter((p) => p.resized)

      // A plain string rendered as text, so no HTML entity here — it would
      // show literally.
      if (saved.length > 0) setShrinking(`Uploading, ${describeSaving(saved[0])}`)

      try {
        return await uploadMedia(prepared.map((p) => p.file))
      } finally {
        setShrinking(null)
      }
    },
    onMutate: () => setError(null),
    onSuccess: ({ uploaded, refused }) => {
      queryClient.invalidateQueries({ queryKey: ['media'] })

      // A refusal here is the whole outcome — one file was picked and it did
      // not land. Silence would leave the field empty with no explanation.
      if (uploaded.length === 0 && refused.length > 0) {
        setError(refused[0])
        return
      }

      // Straight into the field. Uploading a photo to then hunt for it in a
      // grid of 183 is a step nobody would choose.
      if (uploaded[0]) onChoose(uploaded[0].url)
    },
    onError: (e: Error) => setError(e.message),
  })

  const panel = useRef<HTMLDivElement>(null)
  const pressedBackdrop = useRef(false)

  useModalShell({ panel, onDismiss: onClose })

  const matches = useMemo(() => filter(data ?? [], query), [data, query])

  return (
    /*
      <b>The role belongs to the panel, not the dim area</b> (D-515). It sat on
      the overlay, so the thing announced as the dialog was the backdrop and the
      dialog itself was an anonymous group inside it. Nothing closed this on
      Escape either, and Tab walked out of it into the page behind.

      The markup stays where it is — the panel is a flex column that scrolls a
      photo grid, and the shared shell would have to reproduce that exactly to
      gain nothing visible.
    */
    <div
      onMouseDown={(e) => {
        pressedBackdrop.current = e.target === e.currentTarget
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && pressedBackdrop.current) onClose()
      }}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/45 p-0 sm:items-center sm:p-6"
    >
      <div
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Choose a photo"
        className="flex max-h-[88vh] w-full max-w-[52rem] flex-col rounded-t-2xl border border-line bg-surface sm:rounded-2xl"
      >
        <header className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3">
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-lg">Choose a photo</h2>
            <p className="text-[12.5px] text-ink-3">
              {data ? `${data.length} in your library` : 'Loading your library...'}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-ink-3 hover:bg-surface-2 hover:text-ink"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </header>

        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
          <div className="flex min-w-[12rem] flex-1 items-center gap-2 rounded-lg border border-line-2 bg-surface-2 px-2.5 py-1.5 focus-within:border-accent">
            <Search size={14} strokeWidth={2} className="shrink-0 text-ink-3" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name"
              aria-label="Search your photos"
              className="min-w-0 flex-1 bg-transparent text-[13px] outline-none"
            />
          </div>

          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => {
              const files = Array.from(e.target.files ?? [])
              if (files.length > 0) upload.mutate(files)
              e.target.value = ''
            }}
          />

          <button
            type="button"
            disabled={upload.isPending}
            onClick={() => fileInput.current?.click()}
            className="flex shrink-0 items-center gap-1.5 rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3 disabled:opacity-40"
          >
            <Upload size={13} strokeWidth={2} />
            {shrinking ?? (upload.isPending ? 'Uploading...' : 'Upload a new one')}
          </button>
        </div>

        {error && (
          <p role="alert" className="border-b border-line px-4 py-2.5 text-[13px] text-crit">
            {error}
          </p>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {isPending && <p className="text-[13px] text-ink-3">Loading...</p>}
          {isError && <p className="text-[13px] text-crit">Could not load your photos.</p>}

          {data && matches.length === 0 && (
            <p className="py-8 text-center text-[13px] text-ink-3">
              {query ? `Nothing matching "${query}".` : 'Nothing here yet. Upload a photo to start.'}
            </p>
          )}

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {matches.map((asset) => {
              const chosen = asset.url === current

              return (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() => onChoose(asset.url)}
                  aria-pressed={chosen}
                  className={`overflow-hidden rounded-xl border-2 text-left transition ${
                    chosen ? 'border-accent' : 'border-transparent hover:border-line-2'
                  }`}
                >
                  <StudioImage
                    src={asset.url}
                    alt={asset.altText ?? ''}
                    framing={asset.framing}
                    className="aspect-square w-full bg-surface-2"
                  />

                  <span className="block truncate px-1.5 pt-1.5 text-[11.5px] font-semibold">
                    {displayName(asset.fileName)}
                  </span>
                  <span className="block px-1.5 pb-1.5 text-[11px] text-ink-3">
                    {chosen ? 'In use here' : fileSize(asset.sizeBytes)}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Search across the filename and anything written about the photo.
 *
 * `DSC02682.jpg` is unsearchable by design — a camera named it. Alt text and
 * captions are the only words a person ever attached to these, so they have to
 * be searched too or the box only works for the handful with useful names.
 */
function filter(assets: MediaAsset[], query: string): MediaAsset[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return assets

  return assets.filter((a) =>
    `${a.fileName} ${a.altText ?? ''} ${a.caption ?? ''}`.toLowerCase().includes(needle),
  )
}
